import {
  AUTOMATIC_METRICS_OPTIONS,
  AutomaticMetricsType,
  CONCENTRIC_VELOCITY_CARDS,
  EXPERIMENTAL_AUTOMATIC_EXERCISES_GYM_IDS,
} from '../constants';

import type {
  AutomaticMetricsOption,
  ConcentricCard,
  Exercise,
  Metric,
  SetMetrics,
  DeprecatedSetMetrics,
} from '../types';

export type SetMetricPhase = 'concentric' | 'eccentric';

const findTempoOptionWithSameTitle = (
  option: AutomaticMetricsOption,
): AutomaticMetricsOption | undefined => {
  return AUTOMATIC_METRICS_OPTIONS[AutomaticMetricsType.Tempo].find(
    (candidate) =>
      candidate.texts.gym.title === option.texts.gym.title &&
      candidate.texts.school.title === option.texts.school.title,
  );
};

export const getCorrespondingTempoMetric = (
  option: AutomaticMetricsOption,
  phase: SetMetricPhase,
): Metric | null =>
  findTempoOptionWithSameTitle(option)?.metrics[phase] ?? null;

export const getSelectedSetMetric = (
  metrics: SetMetrics,
  phase: SetMetricPhase,
): Metric => metrics.velocity?.[phase] ?? metrics[phase];

export const getConcentricCards = (exercise: Exercise): ConcentricCard[] => {
  const exerciseName = exercise.name.trim().toLowerCase();
  const exerciseCards = CONCENTRIC_VELOCITY_CARDS.find(({ exerciseNames }) =>
    exerciseNames.some((name) => name.toLowerCase() === exerciseName),
  );
  if (exerciseCards) return exerciseCards.cards;
  return AUTOMATIC_METRICS_OPTIONS[AutomaticMetricsType.Velocity].map(
    (option) => ({
      title: option.texts.gym.title,
      range: option.texts.gym.concentricRange,
      subtitle: option.texts.gym.subtitle,
      velocity: option.metrics.concentric,
      tempo: getCorrespondingTempoMetric(option, 'concentric')!,
    }),
  );
};

export const isConcentricCardSelected = (
  card: ConcentricCard,
  metrics: SetMetrics,
): boolean => {
  const velocity = metrics.velocity?.concentric;
  return (
    !!velocity &&
    card.velocity.min === velocity.min &&
    card.velocity.max === velocity.max &&
    card.tempo.min === metrics.concentric.min &&
    card.tempo.max === metrics.concentric.max
  );
};

export const updateSetMetricsForSelection = (
  metrics: SetMetrics,
  phase: SetMetricPhase,
  metric: Metric,
  option: AutomaticMetricsOption,
  fallbackTempoMetric: Metric,
): SetMetrics => {
  if (metric.type === AutomaticMetricsType.Velocity) {
    return {
      ...metrics,
      [phase]:
        getCorrespondingTempoMetric(option, phase) ?? fallbackTempoMetric,
      velocity: {
        ...metrics.velocity,
        [phase]: metric,
      },
    };
  }

  const velocity = { ...metrics.velocity };
  delete velocity[phase];
  const nextMetrics: SetMetrics = { ...metrics, [phase]: metric };
  if (Object.keys(velocity).length > 0) {
    nextMetrics.velocity = velocity;
  } else {
    delete nextMetrics.velocity;
  }
  return nextMetrics;
};

export const isDeprecatedMetrics = (
  metrics: any,
): metrics is Partial<DeprecatedSetMetrics> => {
  return (
    'minConcentric' in metrics ||
    'maxConcentric' in metrics ||
    'minEccentric' in metrics ||
    'maxEccentric' in metrics
  );
};

export const convertDeprecatedSetMetricsToNewFormat = (
  metrics: any,
  exercise: Exercise,
  gymId: string,
): SetMetrics | null => {
  if (
    !metrics ||
    !exercise.analysisOptions ||
    !isExerciseAutomatic(exercise, gymId)
  )
    return null;
  const defaultMetrics = getDefaultSetMetricsFromExercise(exercise, gymId);
  if (!defaultMetrics) return null;

  // If metrics is empty, return default metrics from exercise
  if (Object.keys(metrics).length === 0) {
    return defaultMetrics;
  }

  // If metrics is deprecated, convert to new format
  if (isDeprecatedMetrics(metrics)) {
    const defaultTempoMetrics = exercise.analysisOptions.defaultMetrics.tempo;
    const newMetrics: SetMetrics = {
      concentric: {
        type: AutomaticMetricsType.Tempo,
        min: metrics.minConcentric ?? defaultTempoMetrics.concentric.min,
        max: metrics.maxConcentric ?? defaultTempoMetrics.concentric.max,
      },
      eccentric: {
        type: AutomaticMetricsType.Tempo,
        min: metrics.minEccentric ?? defaultTempoMetrics.eccentric.min,
        max: metrics.maxEccentric ?? defaultTempoMetrics.eccentric.max,
      },
    };
    // TODO: Now need to make sure all min values are less than max values, else min should be set to 0
    if (newMetrics.concentric.min > newMetrics.concentric.max) {
      newMetrics.concentric.min = 0;
    }
    if (newMetrics.eccentric.min > newMetrics.eccentric.max) {
      newMetrics.eccentric.min = 0;
    }
    return newMetrics;
  }

  const velocity: NonNullable<SetMetrics['velocity']> = {
    ...metrics.velocity,
  };
  const normalizedMetrics = (
    ['concentric', 'eccentric'] as SetMetricPhase[]
  ).reduce<SetMetrics>(
    (result, phase) => {
      const storedMetric = metrics[phase] ?? defaultMetrics[phase];
      if (storedMetric.type === AutomaticMetricsType.Velocity) {
        velocity[phase] = storedMetric;
        // Older payloads did not retain the selected preset title, so there is
        // no reliable title match. Preserve velocity for the web UI and use
        // the exercise's configured tempo default for the canonical value.
        result[phase] = defaultMetrics[phase];
      } else {
        result[phase] = storedMetric;
      }
      return result;
    },
    {
      concentric: defaultMetrics.concentric,
      eccentric: defaultMetrics.eccentric,
    },
  );
  if (Object.keys(velocity).length > 0) {
    normalizedMetrics.velocity = velocity;
  }

  // TODO: Now need to make sure all min values are less than max values, else min should be set to 0
  if (normalizedMetrics.concentric.min > normalizedMetrics.concentric.max) {
    normalizedMetrics.concentric.min = 0;
  }
  if (normalizedMetrics.eccentric.min > normalizedMetrics.eccentric.max) {
    normalizedMetrics.eccentric.min = 0;
  }

  Object.values(normalizedMetrics.velocity ?? {}).forEach((metric) => {
    if (metric && metric.min > metric.max) metric.min = 0;
  });

  return normalizedMetrics;
};

export const getDefaultSetMetricsFromExercise = (
  exercise: Exercise,
  gymId: string,
): SetMetrics | null => {
  const { analysisOptions } = exercise;
  if (!analysisOptions || !isExerciseAutomatic(exercise, gymId)) {
    return null;
  }
  const metrics: SetMetrics = {
    concentric: {
      type: AutomaticMetricsType.Tempo,
      min: analysisOptions.defaultMetrics.tempo.concentric.min,
      max: analysisOptions.defaultMetrics.tempo.concentric.max,
    },
    eccentric: {
      type: AutomaticMetricsType.Tempo,
      min: analysisOptions.defaultMetrics.tempo.eccentric.min,
      max: analysisOptions.defaultMetrics.tempo.eccentric.max,
    },
  };
  const velocity: NonNullable<SetMetrics['velocity']> = {};
  if (
    analysisOptions.defaultMetrics.concentricType ===
    AutomaticMetricsType.Velocity
  ) {
    velocity.concentric = {
      type: AutomaticMetricsType.Velocity,
      min: analysisOptions.defaultMetrics.velocity.concentric.min,
      max: analysisOptions.defaultMetrics.velocity.concentric.max,
    };
  }
  if (
    analysisOptions.defaultMetrics.eccentricType ===
    AutomaticMetricsType.Velocity
  ) {
    velocity.eccentric = {
      type: AutomaticMetricsType.Velocity,
      min: analysisOptions.defaultMetrics.velocity.eccentric.min,
      max: analysisOptions.defaultMetrics.velocity.eccentric.max,
    };
  }
  if (Object.keys(velocity).length > 0) metrics.velocity = velocity;
  return metrics;
};

export const isExerciseAutomatic = (
  exercise: Exercise,
  gymId: string,
): boolean => {
  return (
    !!exercise.analysisOptions?.supported ||
    (!!exercise.analysisOptions?.supportedForTesting &&
      EXPERIMENTAL_AUTOMATIC_EXERCISES_GYM_IDS.includes(gymId))
  );
};
